import { registerServiceWorker as registerWebServiceWorker, requestNotificationPermission as requestWebToken } from './firebase';

type PermissionState = 'granted' | 'denied' | 'default';
export type NotificationPlatform = 'web' | 'android' | 'ios';

function getCapacitor(): any {
  if (typeof window !== 'undefined' && (window as any).Capacitor) {
    return (window as any).Capacitor;
  }
  return null;
}

function getPushNotifications(): any {
  const Capacitor = getCapacitor();
  if (!Capacitor) return null;
  
  if (Capacitor.Plugins?.PushNotifications) {
    return Capacitor.Plugins.PushNotifications;
  }
  
  if ((window as any).PushNotifications) {
    return (window as any).PushNotifications;
  }
  
  try {
    const plugin = require('@capacitor/push-notifications');
    return plugin?.PushNotifications || plugin;
  } catch {
    return null;
  }
}

function getFirebaseMessaging(): any {
  const Capacitor = getCapacitor();
  if (!Capacitor) return null;
  
  if (Capacitor.Plugins?.FirebaseMessaging) {
    return Capacitor.Plugins.FirebaseMessaging;
  }
  
  if ((window as any).FirebaseMessaging) {
    return (window as any).FirebaseMessaging;
  }
  
  try {
    const plugin = require('@capacitor-firebase/messaging');
    return plugin?.FirebaseMessaging || plugin;
  } catch {
    return null;
  }
}

function isNativePlatform(): boolean {
  try {
    const Capacitor = getCapacitor();
    if (!Capacitor) return false;
    return Capacitor.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

function resolveNativePlatform(): NotificationPlatform {
  try {
    const Capacitor = getCapacitor();
    if (!Capacitor) return 'web';
    const platform = Capacitor.getPlatform?.();
    return platform === 'ios' ? 'ios' : 'android';
  } catch {
    return 'web';
  }
}

export async function getNotificationPermissionState(): Promise<PermissionState> {
  if (!isNativePlatform()) {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      return 'default';
    }
    return Notification.permission;
  }

  const PushNotifications = getPushNotifications();
  if (!PushNotifications) {
    return 'default';
  }

  const status = await PushNotifications.checkPermissions();
  if (status.receive === 'granted') {
    return 'granted';
  }
  if (status.receive === 'denied') {
    return 'denied';
  }
  return 'default';
}

export async function prepareNotificationRuntime(): Promise<void> {
  if (!isNativePlatform()) {
    await registerWebServiceWorker();
    return;
  }

  const PushNotifications = getPushNotifications();
  if (PushNotifications) {
    await PushNotifications.removeAllListeners();
  }
}

export async function requestNotificationRegistration(): Promise<{ token: string; platform: NotificationPlatform } | null> {
  const isNative = isNativePlatform();
  console.log('[Notifications] Platform detection:', { isNative, platform: resolveNativePlatform() });
  
  if (!isNative) {
    console.log('[Notifications] Using web notification flow');
    const token = await requestWebToken();
    if (!token) {
      console.warn('[Notifications] Failed to get web FCM token');
      return null;
    }
    return { token, platform: 'web' };
  }

  console.log('[Notifications] Using native notification flow');
  const PushNotifications = getPushNotifications();
  if (!PushNotifications) {
    console.error('[Notifications] PushNotifications plugin not available');
    const Capacitor = getCapacitor();
    console.log('[Notifications] Capacitor available:', !!Capacitor);
    console.log('[Notifications] Capacitor.Plugins:', Capacitor?.Plugins ? Object.keys(Capacitor.Plugins) : 'not available');
    return null;
  }
  console.log('[Notifications] PushNotifications plugin loaded');

  const permission = await PushNotifications.requestPermissions();
  console.log('[Notifications] Permission request result:', permission);
  if (permission.receive !== 'granted') {
    console.warn('[Notifications] Notification permission not granted:', permission.receive);
    return null;
  }

  await PushNotifications.register();
  console.log('[Notifications] Push notifications registered');

  const FirebaseMessaging = getFirebaseMessaging();
  if (!FirebaseMessaging) {
    console.error('[Notifications] FirebaseMessaging plugin not available');
    const Capacitor = getCapacitor();
    console.log('[Notifications] Capacitor.Plugins:', Capacitor?.Plugins ? Object.keys(Capacitor.Plugins) : 'not available');
    return null;
  }
  console.log('[Notifications] FirebaseMessaging plugin loaded');

  const result = await FirebaseMessaging.getToken();
  console.log('[Notifications] Firebase token result:', { hasToken: !!result.token });
  if (!result.token) {
    console.warn('[Notifications] Firebase token is empty');
    return null;
  }

  const platform = resolveNativePlatform();
  console.log('[Notifications] ✅ Token obtained successfully for platform:', platform);
  return {
    token: result.token,
    platform
  };
}

export async function onForegroundNativeNotification(callback: (payload: { title?: string; body?: string; data?: Record<string, unknown> }) => void) {
  if (!isNativePlatform()) {
    return () => undefined;
  }

  const PushNotifications = getPushNotifications();
  if (!PushNotifications) {
    return () => undefined;
  }

  const listener = PushNotifications.addListener('pushNotificationReceived', (event) => {
    callback({
      title: event.title ?? undefined,
      body: event.body ?? undefined,
      data: event.data ?? undefined
    });
  });

  return () => {
    listener.then((handle) => handle.remove()).catch(() => undefined);
  };
}

export async function onBackgroundNativeNotification(callback: (payload: { title?: string; body?: string; data?: Record<string, unknown> }) => void) {
  if (!isNativePlatform()) {
    return () => undefined;
  }

  const PushNotifications = getPushNotifications();
  if (!PushNotifications) {
    return () => undefined;
  }

  const listener = PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    callback({
      title: event.notification?.title ?? undefined,
      body: event.notification?.body ?? undefined,
      data: event.notification?.data ?? undefined
    });
  });

  return () => {
    listener.then((handle) => handle.remove()).catch(() => undefined);
  };
}

export async function updateAppBadge(count: number): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    const Capacitor = getCapacitor();
    if (!Capacitor) return;

    const platform = Capacitor.getPlatform?.();
    if (platform === 'android') {
      const PushNotifications = getPushNotifications();
      if (PushNotifications && PushNotifications.setBadgeCount) {
        await PushNotifications.setBadgeCount({ count });
        console.log('[Notifications] Badge updated to:', count);
      } else {
        console.warn('[Notifications] Badge API not available');
      }
    } else if (platform === 'ios') {
      const PushNotifications = getPushNotifications();
      if (PushNotifications && PushNotifications.setBadgeCount) {
        await PushNotifications.setBadgeCount({ count });
        console.log('[Notifications] Badge updated to:', count);
      }
    }
  } catch (error) {
    console.error('[Notifications] Failed to update badge:', error);
  }
}

